import {
  errorMap,
  Form,
  Main,
  Padding,
  Stack,
  TotalEntries,
  useDirectory,
  ValidPeriodOptions,
  DataObjectVersionFilters,
  DataObjectDescriptionFilter,
  EMPTY_PAGE,
  useValidationDashboardsQuery,
  DashboardType,
  omit,
  VisualValidationFilters,
  VISUAL_VALIDATION_FILTER_KEYS,
  CustomPagination,
  zIsoDate,
  useDataObjectGroupsWithDescriptionsQuery,
  useGroupDescriptionOptions,
  DataObjectGroupType,
  useGenerateVisualValidationCrosstableMutation,
  useDataObjectVersionsQuery,
  useDuplicateVisualValidationCrosstableMutation,
  type ISODate,
  useChangeVisualValidationCrosstableDataMutation,
  useVisualValidationDashboardActions,
  ValidationDashboardListDto,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import styled from 'styled-components';
import { path } from '../../../paths';

import translations from './CrossTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import z from 'zod/v3';
import { Divider, Flex } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { VisualValidationHeader } from '../VisualValidationHeader';
import { VisualValidationActionButtons } from '../VisualValidationActionButtons';
import { ValidationDashboardsTable } from '../ValidationDashboardsTable';
import { keepPreviousData } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { CommentsModal, hasDescriptionId } from '../modals/CommentsModal';
import {
  AlgorithmsModal,
  ModalData,
  SourcesModal,
} from '../modals/DetailsModal';

const { addTranslations, t } = i18nNsFactory('visual-validation-cross-table');
addTranslations(translations);

type CrossTableFilters = VisualValidationFilters & {
  valueDate?: string;
  version?: string;
};

const FILTER_KEYS: (keyof CrossTableFilters)[] = [
  ...VISUAL_VALIDATION_FILTER_KEYS,
  'valueDate',
  'version',
];

export const CrossTableFormSchema = z.object({
  groupDescriptionRef: z
    .string(errorMap(() => t('validation.groupDescriptionRef.required')))
    .min(1),
  valueDate: zIsoDate(() => t('validation.valueDate.required')),
  version: z.number(errorMap(() => t('validation.version.required'))).min(1),
});
type CrossTableFormType = z.infer<typeof CrossTableFormSchema>;

export const CrossTable = () => (
  <Form schema={CrossTableFormSchema} blockNavigationOnFirstChange={false}>
    <Main>
      <CrossTableInner />
    </Main>
  </Form>
);

const CrossTableInner = () => {
  const directoryProps = useDirectory({
    queryFilterKeys: FILTER_KEYS,
  });
  const {
    queryParameters,
    setQueryFilters,
    tableProps,
    headerProps,
    setTableFilters,
    paginationProps,
  } = directoryProps;

  const { handleSubmit, getValues, trigger } =
    useFormContext<CrossTableFormType>();
  const [periodOptions, setPeriodOptions] = useState<ValidPeriodOptions>({});
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [algorithmsModalData, setAlgorithmsModalData] =
    useState<ModalData | null>(null);
  const [sourcesModalData, setSourcesModalData] = useState<ModalData | null>(
    null,
  );

  const {
    selectedValueDate,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    versions,
    selectedVersion,
    isFetchingVersions,
    versionsEnabled,
    refetchDescriptions,
  } = useCrossTableSelection(queryParameters, setQueryFilters);
  const descriptionId = selectedDescription?.descriptionId;

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useValidationDashboardsQuery(
    {
      ...omit(queryParameters, ['groupDescriptionRef']),
      dataObjectDescriptionId: descriptionId!,
      dashboardType: DashboardType.CROSSTABLE,
    },
    {
      placeholderData: keepPreviousData,
      enabled: !!descriptionId,
    },
  );

  const getRequestData = useCallback(
    () => ({
      dataObjectVersionId: getValues('version'),
    }),
    [getValues],
  );

  const openDetailsModal = useCallback(
    (
      modalType: 'algorithms' | 'sources',
      rowData?: ValidationDashboardListDto,
    ) => {
      if (!selectedDescription) return;
      const version = rowData
        ? rowData.versions[0]?.dataObjectVersion
        : selectedVersion;
      if (!version) return;
      const data = {
        type: DashboardType.CROSSTABLE,
        version,
        descriptionOption: selectedDescription,
      } as const;
      if (modalType === 'algorithms') setAlgorithmsModalData(data);
      else setSourcesModalData(data);
    },
    [selectedDescription, selectedVersion],
  );

  const {
    createDashboard,
    openDashboard,
    duplicateDashboard,
    changeDashboardData,
  } = useVisualValidationDashboardActions<{ dataObjectVersionId: number }>({
    hasAccess: selectedDescription?.hasAccess || false,
    popupWindowPath: path.visualValidationGenerating,
    getRequestData,
    useCreate: useGenerateVisualValidationCrosstableMutation,
    useDuplicate: useDuplicateVisualValidationCrosstableMutation,
    useChangeData: useChangeVisualValidationCrosstableDataMutation,
    onValidationFailed: trigger,
    onSuccess: refetch,
  });

  return (
    <Main.Col>
      <Main.Header />
      <Main.Content padding={false} id="visual-validation-cross-table">
        {isCommentsModalOpen && selectedDescription && (
          <CommentsModal
            descriptionOption={selectedDescription}
            type="single"
            valueDate={selectedValueDate}
            refetchDescriptions={refetchDescriptions}
            onClose={() => setIsCommentsModalOpen(false)}
          />
        )}
        {algorithmsModalData && (
          <AlgorithmsModal
            {...algorithmsModalData}
            onClose={() => setAlgorithmsModalData(null)}
          />
        )}
        {sourcesModalData && (
          <SourcesModal
            {...sourcesModalData}
            onClose={() => setSourcesModalData(null)}
          />
        )}
        <ContentWrapper>
          <VisualValidationHeader
            infoIconUrl={InfoIconUrl.VISUAL_VALIDATION_CROSS_TABLE}
          />
          <Padding horizontal>
            <DataObjectDescriptionFilter
              options={descriptionOptions}
              isLoading={isFetchingDescriptions}
              onChange={() => setPeriodOptions({})}
            />
          </Padding>
          <Divider />
          <Padding horizontal>
            <Flex gap="small" wrap="wrap" justify="space-between">
              <Flex gap="small" wrap="wrap">
                <DataObjectVersionFilters
                  versions={versions || []}
                  isLoading={isFetchingVersions || !versionsEnabled}
                  periodOptions={periodOptions}
                  setPeriodOptions={setPeriodOptions}
                />
              </Flex>
              <VisualValidationActionButtons
                hasDescription={!!descriptionId}
                hasVersions={!!selectedVersion}
                hasComments={selectedDescription?.hasComments || false}
                onCommentsClick={() => {
                  setIsCommentsModalOpen(true);
                }}
                onAlgorithmsClick={() => openDetailsModal('algorithms')}
                onSourcesClick={() => openDetailsModal('sources')}
                onNewDashboardClick={() => handleSubmit(createDashboard)()}
                filtersCount={headerProps.filtersCount}
                onFilterClear={headerProps.onFilterClear}
              />
            </Flex>
          </Padding>
          <Padding horizontal bottom>
            <Flex vertical gap="small">
              <ValidationDashboardsTable
                data={content || []}
                isLoading={isFetching}
                setFilters={setTableFilters}
                scrollContainerSelector="#visual-validation-cross-table"
                refetchData={refetch}
                onClickOpen={openDashboard}
                onClickDuplicate={duplicateDashboard}
                onClickChangeData={changeDashboardData}
                onClickSourceData={(d) => openDetailsModal('sources', d)}
                onClickAlgorithmData={(d) => openDetailsModal('algorithms', d)}
                {...tableProps}
              />
            </Flex>
          </Padding>
        </ContentWrapper>
      </Main.Content>
      <Main.Footer>
        <TotalEntries count={totalElements} />
        <FooterPagination {...paginationProps} />
      </Main.Footer>
    </Main.Col>
  );
};

const useCrossTableSelection = (
  queryParameters: Partial<CrossTableFilters>,
  setQueryFilters: (filters: Partial<CrossTableFilters>) => void,
) => {
  const { groupDescriptionRef, valueDate, version } =
    useWatch<CrossTableFormType>() || {};
  const { reset } = useFormContext<CrossTableFormType>();

  // Update query parameters when form values change
  useEffect(() => {
    setQueryFilters({
      groupDescriptionRef,
      valueDate,
      version: version ? String(version) : undefined,
    });
  }, [groupDescriptionRef, valueDate, version, setQueryFilters]);

  // Reset form values when query parameters change
  useEffect(() => {
    const valueDateParam = queryParameters?.valueDate;
    const versionParam = queryParameters?.version;

    reset({
      ...queryParameters,
      valueDate: (valueDateParam as ISODate) || undefined,
      version: versionParam ? Number(versionParam) : undefined,
    });
  }, [queryParameters, reset]);

  const {
    data: dataObjectGroupsWithDescriptions,
    refetch: refetchDescriptions,
    isFetching: isFetchingDescriptions,
  } = useDataObjectGroupsWithDescriptionsQuery(
    {
      groupType: DataObjectGroupType.O,
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  const descriptionOptions = useGroupDescriptionOptions(
    dataObjectGroupsWithDescriptions,
    false,
  );

  const selectedDescription = useMemo(
    () =>
      descriptionOptions
        .flatMap((options) => options)
        .filter(hasDescriptionId)
        .find((option) => option.value === groupDescriptionRef),
    [descriptionOptions, groupDescriptionRef],
  );
  const descriptionId = selectedDescription?.descriptionId;

  const versionsEnabled = !!descriptionId && !!valueDate;
  const { data: versions = [], isFetching: isFetchingVersions } =
    useDataObjectVersionsQuery(
      {
        id: descriptionId!,
        valueDate,
      },
      {
        enabled: versionsEnabled,
        placeholderData: keepPreviousData,
      },
    );
  const selectedVersion = useMemo(
    () => versions?.find((ver) => ver.id === version),
    [versions, version],
  );

  return {
    selectedValueDate: valueDate,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    refetchDescriptions,
    versions,
    selectedVersion,
    isFetchingVersions,
    versionsEnabled,
  };
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
